import { EventSource } from "../event-source";
import { Route, RoutePattern, RouteOptions } from './route';
import { ViewConstructor } from "../view";
import { BaseApp } from '../app';

export default class Router extends EventSource
{
    private routes: Route[];
    private app: BaseApp;
    private enabled: boolean;
    public poppedState;
    private navigationsSinceHome: number;

    private goTo: {
        hash?: string,
        data?: any
    } = {};

    private cleanedLocation: string;
    private _path: string;  
    public get path(): string { return this._path; }
    public set path(value: string)
    {
        if (value !== this._path)
        {
            this._path = value;
        }
    }

    /**
     * Creates a new instance of Router.
     * @param app App
     */
    constructor(app: BaseApp)
    {
        super();
        this.app = app;
        this.routes = [];
        this.enabled = false;

        window.addEventListener('hashchange', this.onWindowHashChange, false);
        window.addEventListener('popstate', event => {
            this.poppedState = event.state;
        });
        this.onWindowHashChange(null);
    }

    public setHome() {
        console.log(`Home set at ${location.hash}`);
        this.navigationsSinceHome = 0;
    }

    /** Reacts to window hashchange events and switches to the view when the hash changes. */
    private onWindowHashChange = (e: Event) => 
    {
        console.log('Navigation event: ', window.location.hash);

        let data = null;
        if (decodeURI(window.location.hash) == this.goTo.hash)
        {
            data = this.goTo.data;
            this.goTo.hash = null;
            this.goTo.data = null;
        }

        let parts = window.location.hash.split('#');
        this.path = parts.length > 1 ? parts[1] : '';

        if (this.enabled)
        {
            this.run(data);
        }
    }

    /**
     * Adds a route configuration to the router.
     * @param pattern Pattern used to trigger the view.
     * @param viewType View constructor.
     * @param initial Initial data used as the view context data.
     */
    public add(pattern: RoutePattern, viewType: ViewConstructor, options?: RouteOptions): Router
    /**
     * Adds a route configuration to the router.
     * @param pattern Regular expression used to trigger the view.
     * @param viewTypes View constructor types (chain of views).
     * @param initial Initial data used as the view context data.
     */
    public add(pattern: RoutePattern, viewTypes: ViewConstructor[], options?: RouteOptions): Router
    public add(pattern: RoutePattern, viewTypes: ViewConstructor | ViewConstructor[], options?: RouteOptions): Router
    {
        if (!(pattern instanceof RegExp))
        {
            pattern = new RegExp(`^${pattern}$`);
        }
            
        let route = new Route(this.app, pattern, viewTypes, options);

        this.routes.push(route);

        return this;
    }

    /** Runs the router, executes the first matching view. */
    public run(data?: any): PromiseLike<void>
    {
        this.app.modals.closeAll();
        
        let poppedState = this.poppedState;
        this.poppedState = null;

        this.trigger('run');
        this.enabled = true;
        let result: PromiseLike<void> = Promise.resolve();

        for (let route of this.routes)
        {
            if (route.match(this.path))
            {
                try
                {
                    result = route.run(data || poppedState);
                }
                catch (exception)
                {
                    if (this.app.settings.debug)
                    {
                        this.app.error(exception);
                    }
                    else
                    {
                        this.app.error(exception);
                    }
                }
                finally
                {
                    return result;
                }
            }
        }

        this.app.warning(new Error(`Cound not find "${this.path}".`));
    }

    public go(hash: string, data?: any)
    {
        if (this.navigationsSinceHome !== undefined) {
            this.navigationsSinceHome++;
        }
        this.goTo.hash = hash;
        this.goTo.data = data;
        let dataCopy = JSON.parse(JSON.stringify(this.app.currentView.data));
        history.replaceState(dataCopy, document.title, location.href);
        window.location.hash = hash;
    }

    public back() {
        if (this.navigationsSinceHome !== undefined) {
            this.navigationsSinceHome = this.navigationsSinceHome > 0 ? this.navigationsSinceHome - 1 : 0;
        }
        history.back();
    }

    public returnHome() {
        console.log(`Returning to home, backing ${this.navigationsSinceHome}`);
        if (this.navigationsSinceHome !== undefined) {
            if (this.navigationsSinceHome > 0) {
                history.go(0 - this.navigationsSinceHome);
            }
        }
    }
}